"use client";

import { ChevronRight, Trash2 } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
} from "@/components/ui/sidebar";
import { NavItem } from "./app-sidebar/app-sidebar";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { RenderIcon } from "./render-icon";
import { useSidebar } from "@/components/ui/sidebar";

interface NavMainProps {
  items: Array<NavItem>;
}

/**
 * Returns the ids of the folders leading to the active item (its ancestor
 * folders), or null if nothing in this subtree is active. Descends first so the
 * deepest active item wins.
 */
const findActivePath = (items: Array<NavItem>, ancestors: string[] = []): string[] | null => {
  for (const item of items) {
    if (item.children?.length) {
      const childAncestors = item.type === "folder" ? [...ancestors, item.id] : ancestors;
      const found = findActivePath(item.children, childAncestors);
      if (found) return found;
    }
    if (item.active) return ancestors;
  }
  return null;
};

export function NavMain({ items }: NavMainProps) {
  // The set of open folder ids. Folders open/close independently — multiple
  // branches can be open at once.
  const pathname = usePathname();
  const activePath = useMemo(() => findActivePath(items) ?? [], [items]);

  // Re-expand the active branch whenever the route changes (even between entities
  // in the same folder) or the folder data resolves after mount. Keyed on the
  // pathname too, so re-opening works even if the active folder path is unchanged.
  const triggerKey = `${pathname}|${activePath.join("/")}`;

  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(activePath));
  const [prevTriggerKey, setPrevTriggerKey] = useState(triggerKey);

  // Follow navigation without a useEffect: expand the folders leading to the
  // active item without collapsing anything the user already opened. React
  // re-renders synchronously here, so the tree never paints in a stale state.
  if (prevTriggerKey !== triggerKey) {
    setPrevTriggerKey(triggerKey);
    if (activePath.length) {
      setOpenIds((prev) => {
        const next = new Set(prev);
        activePath.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  return (
    <SidebarGroup>
      {/* <SidebarGroupLabel>Platform</SidebarGroupLabel> */}
      <SidebarMenu>
        {items.map((item) => (
          <NavItemNode key={item.id} item={item} openIds={openIds} setOpenIds={setOpenIds} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

const NavItemNode = ({
  item,
  openIds,
  setOpenIds,
  setIdsToDelete,
}: {
  item: NavItem;
  openIds: Set<string>;
  setOpenIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setIdsToDelete?: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { setOpenMobile } = useSidebar();
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Scroll the active item into view on navigation. A rAF lets the containing
  // folder finish expanding before we measure its final position.
  useEffect(() => {
    if (item.active && item.type !== "folder") {
      const raf = requestAnimationFrame(() => {
        linkRef.current?.scrollIntoView({ block: "nearest" });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [item.active, item.type]);

  // Handle folder/item with children
  if (item.type === "folder") {
    const open = openIds.has(item.id);
    return (
      <Collapsible
        key={item.id}
        asChild
        open={open}
        onOpenChange={(next) =>
          setOpenIds((prev) => {
            const updated = new Set(prev);
            if (next) updated.add(item.id);
            else updated.delete(item.id);
            return updated;
          })
        }
      >
        <SidebarMenuItem>
          <SidebarMenuButton tooltip={item.title} asChild>
            <CollapsibleTrigger className="group flex items-center gap-2 text-muted-foreground">
              <RenderIcon iconName={item.icon ?? ""} size={14} className="text-muted-foreground" />
              <div className="mr-4 w-full overflow-hidden">{item.title}</div>
              <SidebarMenuAction asChild className={cn({ "rotate-90": open })}>
                <ChevronRight />
              </SidebarMenuAction>
            </CollapsibleTrigger>
          </SidebarMenuButton>

          <CollapsibleContent>
            {item?.children && item?.children?.length > 0 && (
              <SidebarMenuSub className="pl-2 pr-0">
                {item.children.map((subItem) => (
                  <NavItemNode
                    key={subItem.id}
                    item={subItem}
                    openIds={openIds}
                    setOpenIds={setOpenIds}
                    setIdsToDelete={setIdsToDelete}
                  />
                ))}
              </SidebarMenuSub>
            )}
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  // Handle leaf item (no children)
  return (
    <SidebarMenuButton tooltip={item.title} asChild>
      <Link
        ref={linkRef}
        href={item.url}
        onClick={() => setOpenMobile(false)}
        className={cn("group flex items-center gap-2 text-muted-foreground", {
          "bg-sidebar-accent": item.active,
        })}
      >
        <RenderIcon
          iconName={item.icon || "FileSpreadsheet"}
          size={14}
          className="text-muted-foreground"
        />
        <div className="mr-4 w-full overflow-hidden">{item.title}</div>
        {setIdsToDelete && (
          <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 group-hover:block">
            <button
              type="button"
              className="flex items-center justify-items-center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (setIdsToDelete) {
                  setIdsToDelete([item.id]);
                }
              }}
            >
              <Trash2 size={14} className="text-gray-400" />
            </button>
          </div>
        )}
      </Link>
    </SidebarMenuButton>
  );
};
